import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit, Trash2, Loader2, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { productsAPI } from '../../services/api';
import toast from 'react-hot-toast';

// Image URL helper function - HTTPS uyumlu
const getImageUrl = (imagePath) => {
  if (!imagePath) return '';
  
  // Production'da HTTPS kullan
  if (process.env.NODE_ENV === 'production') {
    // HTTPS ile başlamıyorsa HTTPS ekle
    if (imagePath.startsWith('http://')) {
      return imagePath.replace('http://', 'https://');
    }
    // HTTPS ile başlıyorsa direkt döndür
    if (imagePath.startsWith('https://')) {
      return imagePath;
    }
    // Relative path ise HTTPS domain ile birleştir
    if (imagePath.startsWith('/')) {
      return `https://leasertech.com${imagePath}`;
    }
    return `https://leasertech.com/${imagePath}`;
  }
  
  // Development'ta localhost kullan
  const API_BASE_URL = 'http://localhost:5000';
  if (imagePath.startsWith('http')) {
    return imagePath;
  }
  return `${API_BASE_URL}${imagePath}`;
};

// Loading Button Component
const LoadingButton = ({ loading, children, ...props }) => (
  <button
    {...props}
    disabled={loading}
    className={`flex items-center justify-center px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
      loading
        ? 'bg-gray-400 cursor-not-allowed'
        : 'bg-blue-600 hover:bg-blue-700 text-white hover:shadow-lg'
    }`}
  >
    {loading && <Loader2 className="animate-spin h-4 w-4 mr-2" />}
    {children}
  </button>
);

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const isProcessingImages = useRef(false);
  
  // Pagination ve arama state'leri
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    original_price: '',
    categories: [],
    subcategory: '',
    brand: '',
    sku: '',
    stock: '',
    min_stock: '5',
    images: [],
    specifications: [],
    tags: [],
    is_active: true,
    is_featured: false,
    weight: '',
    dimensions_length: '',
    dimensions_width: '',
    dimensions_height: '',
    shipping_free: false,
    shipping_cost: '0',
    estimated_delivery: '3-5 iş günü'
  });

  const categories = [
    'Şarj Aleti',
    'Powerbank', 
    'Kulaklık',
    'Hoparlör',
    'Otomobil Ürünleri',
    'Telefon Tutacakları',
    'Projeksiyon Cihazları',
    'Bilgisayar Aksesuarları',
    'Telefon Aksesuarları',
    'Diğer'
  ];

  // Safari için özel kontrol
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  
  useEffect(() => {
    // Safari'de sürekli yenilenmeyi önle
    if (isSafari && sessionStorage.getItem('admin-products-loaded')) {
      return;
    }
    
    loadProducts();
    
    if (isSafari) {
      sessionStorage.setItem('admin-products-loaded', 'true');
    }
  }, [currentPage, searchTerm, sortBy, sortOrder, isSafari]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      
      // Basit çözüm: Normal products API kullan
      const response = await productsAPI.getProducts({
        page: currentPage,
        limit: 100, // Daha fazla ürün
        search: searchTerm,
        sort: sortBy === 'created_at' ? 'newest' : sortBy
      });
      console.log('Products Response:', response.data);
      
      if (response.data && response.data.products && response.data.pagination) {
        setProducts(response.data.products);
        setTotalPages(response.data.pagination.totalPages);
        setTotalProducts(response.data.pagination.totalProducts);
      } else {
        console.error('Invalid response format:', response.data);
        toast.error('Ürünler yüklenirken format hatası');
      }
    } catch (error) {
      console.error('Ürünler yüklenemedi:', error);
      toast.error('Ürünler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'categories') {
      // Kategori seçimi için özel işlem
      const categoryValue = e.target.value;
      setFormData(prev => ({
        ...prev,
        categories: prev.categories.includes(categoryValue)
          ? prev.categories.filter(cat => cat !== categoryValue)
          : [...prev.categories, categoryValue]
      }));
    } else if (name === 'images') {
      // Çift çağrıları engelle
      if (isProcessingImages.current) {
        console.log('Already processing images, skipping...');
        return;
      }
      
      isProcessingImages.current = true;
      
      // Görsel yükleme için özel işlem
      const files = Array.from(e.target.files);
      console.log('Selected files:', files.length, files.map(f => f.name));
      
      if (files.length === 0) {
        isProcessingImages.current = false;
        return; // Dosya seçilmediyse işlem yapma
      }
      
      // Dosya boyutlarını kontrol et (aynı dosyalar aynı boyutta olur)
      const fileSizes = files.map(f => f.size);
      console.log('File sizes:', fileSizes);
      
      const imageUrls = files.map(file => ({
        url: URL.createObjectURL(file),
        name: file.name,
        file: file,
        size: file.size, // Boyut bilgisini ekle
        id: Date.now() + Math.random() // Benzersiz ID ekle
      }));
      
      setFormData(prev => {
        console.log('Previous images count:', prev.images.length);
        
        // Aynı dosya adı VE boyutuna sahip görselleri filtrele
        const existingFiles = prev.images.map(img => `${img.name}-${img.size || 0}`);
        const newImageUrls = imageUrls.filter(img => {
          const fileKey = `${img.name}-${img.size}`;
          const isDuplicate = existingFiles.includes(fileKey);
          console.log(`Checking ${img.name}: ${isDuplicate ? 'DUPLICATE' : 'NEW'}`);
          return !isDuplicate;
        });
        
        console.log('New unique images count:', newImageUrls.length);
        const newImages = [...prev.images, ...newImageUrls];
        console.log('Final images count:', newImages.length);
        
        // İşlem tamamlandı, flag'i sıfırla
        setTimeout(() => {
          isProcessingImages.current = false;
        }, 100);
        
        return {
          ...prev,
          images: newImages
        };
      });
      
      // Input'u temizle ki aynı dosyalar tekrar eklenmesin
      e.target.value = '';
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const removeImage = (index) => {
    setFormData(prev => {
      const imageToRemove = prev.images[index];
      // Blob URL'yi temizle
      if (imageToRemove && imageToRemove.url && imageToRemove.url.startsWith('blob:')) {
        URL.revokeObjectURL(imageToRemove.url);
      }
      
      return {
        ...prev,
        images: prev.images.filter((_, i) => i !== index)
      };
    });
  };

  const resetForm = () => {
    // Önceki görsellerin blob URL'lerini temizle
    formData.images.forEach(img => {
      if (img.url && img.url.startsWith('blob:')) {
        URL.revokeObjectURL(img.url);
      }
    });
    
    setFormData({
      name: '',
      description: '',
      price: '',
      original_price: '',
      categories: [],
      subcategory: '',
      brand: '',
      sku: '',
      stock: '',
      min_stock: '5',
      images: [],
      specifications: [],
      tags: [],
      is_active: true,
      is_featured: false,
      weight: '',
      dimensions_length: '',
      dimensions_width: '',
      dimensions_height: '',
      shipping_free: false,
      shipping_cost: '0',
      estimated_delivery: '3-5 iş günü'
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Görsel dosyalarını ayır
      const imageFiles = formData.images.filter(img => img.file).map(img => img.file);
      const productData = {
        ...formData,
        price: parseFloat(formData.price),
        original_price: formData.original_price ? parseFloat(formData.original_price) : null,
        stock: parseInt(formData.stock),
        min_stock: parseInt(formData.min_stock),
        weight: formData.weight ? parseFloat(formData.weight) : null,
        dimensions_length: formData.dimensions_length ? parseFloat(formData.dimensions_length) : null,
        dimensions_width: formData.dimensions_width ? parseFloat(formData.dimensions_width) : null,
        dimensions_height: formData.dimensions_height ? parseFloat(formData.dimensions_height) : null,
        shipping_cost: parseFloat(formData.shipping_cost)
        // images alanını kaldırdık - mevcut resimleri korumak için
      };

      let createdProduct;
      if (editingProduct) {
        createdProduct = await productsAPI.updateProduct(editingProduct.id, productData);
        toast.success('Ürün güncellendi');
      } else {
        createdProduct = await productsAPI.createProduct(productData);
        toast.success('Ürün eklendi');
      }

      // Görselleri yükle
      if (imageFiles.length > 0 && createdProduct.data?.product?.id) {
        const formDataImages = new FormData();
        imageFiles.forEach(file => {
          formDataImages.append('images', file);
        });
        
        console.log('Uploading images for product:', createdProduct.data.product.id);
        console.log('Image files count:', imageFiles.length);
        
        await productsAPI.uploadImages(createdProduct.data.product.id, formDataImages);
        toast.success('Görseller yüklendi');
      }

      setShowAddForm(false);
      setEditingProduct(null);
      resetForm();
      loadProducts();
    } catch (error) {
      console.error('Ürün kaydedilemedi:', error);
      console.error('Error details:', error.response?.data);
      const errorMessage = error.response?.data?.message || 'Ürün kaydedilemedi';
      const errorDetails = error.response?.data?.errors || [];
      
      if (errorDetails.length > 0) {
        toast.error(`${errorMessage}: ${errorDetails.map(e => e.msg).join(', ')}`);
      } else {
        toast.error(errorMessage);
      }
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    
    // Mevcut görselleri doğru formatta hazırla - sadece gerçek dosya URL'lerini al
    const existingImages = (product.images || [])
      .filter(img => img.url && !img.url.startsWith('blob:')) // Blob URL'lerini filtrele
      .map(img => ({
        url: img.url,
        name: img.filename || img.originalName || 'Mevcut görsel',
        file: null // Mevcut görseller için file yok
      }));
    
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      original_price: product.original_price ? product.original_price.toString() : '',
      categories: product.categories || (product.category ? [product.category] : []),
      subcategory: product.subcategory || '',
      brand: product.brand,
      sku: product.sku,
      stock: product.stock.toString(),
      min_stock: product.min_stock.toString(),
      images: existingImages,
      specifications: product.specifications || [],
      tags: product.tags || [],
      is_active: product.is_active,
      is_featured: product.is_featured,
      weight: product.weight ? product.weight.toString() : '',
      dimensions_length: product.dimensions_length ? product.dimensions_length.toString() : '',
      dimensions_width: product.dimensions_width ? product.dimensions_width.toString() : '',
      dimensions_height: product.dimensions_height ? product.dimensions_height.toString() : '',
      shipping_free: product.shipping_free,
      shipping_cost: product.shipping_cost.toString(),
      estimated_delivery: product.estimated_delivery
    });
    setShowAddForm(true);
  };

  const handleDelete = async (productId) => {
    if (window.confirm('Bu ürünü silmek istediğinizden emin misiniz?')) {
      try {
        await productsAPI.deleteProduct(productId);
        toast.success('Ürün silindi');
        loadProducts();
      } catch (error) {
        console.error('Ürün silinemedi:', error);
        toast.error('Ürün silinemedi');
      }
    }
  };

  // Arama fonksiyonu
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Arama yapıldığında ilk sayfaya git
  };

  // Sayfa değiştirme fonksiyonu
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Sıralama fonksiyonu
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortOrder('ASC');
    }
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Ürün Yönetimi
          </h1>
          <button
            onClick={() => setShowAddForm(true)}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center gap-2"
          >
            <Plus className="h-5 w-5" />
            Yeni Ürün Ekle
          </button>
        </div>

        {/* Ürün Ekleme/Düzenleme Formu */}
        {showAddForm && (
          <div className="bg-white rounded-lg shadow-md p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {editingProduct ? 'Ürün Düzenle' : 'Yeni Ürün Ekle'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ürün Adı *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Marka *
                  </label>
                  <input
                    type="text"
                    name="brand"
                    value={formData.brand}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Kategoriler * (Birden fazla seçebilirsiniz)
                  </label>
                  <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border border-gray-300 rounded-md p-3">
                    {categories.map(category => (
                      <label key={category} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          name="categories"
                          value={category}
                          checked={formData.categories.includes(category)}
                          onChange={handleInputChange}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700">{category}</span>
                      </label>
                    ))}
                  </div>
                  {formData.categories.length === 0 && (
                    <p className="text-red-500 text-sm mt-1">En az bir kategori seçmelisiniz</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Alt Kategori
                  </label>
                  <input
                    type="text"
                    name="subcategory"
                    value={formData.subcategory}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ürün Görselleri
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <input
                      type="file"
                      name="images"
                      multiple
                      accept="image/*"
                      onChange={handleInputChange}
                      className="hidden"
                      id="image-upload"
                    />
                    <label
                      htmlFor="image-upload"
                      className="cursor-pointer flex flex-col items-center"
                    >
                      <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      <span className="text-sm text-gray-600">Görsel yüklemek için tıklayın</span>
                      <span className="text-xs text-gray-400">PNG, JPG, JPEG formatları desteklenir</span>
                    </label>
                  </div>
                  
                  {formData.images.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">
                        Yüklenen Görseller ({formData.images.length})
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {formData.images.map((image, index) => (
                          <div key={index} className="relative">
                            <img
                              src={image.url.startsWith('http') || image.url.startsWith('blob:') ? image.url : `${image.url}`}
                              alt={`Ürün görseli ${index + 1}`}
                              className="w-full h-24 object-cover rounded-lg border"
                              crossOrigin="anonymous"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'block';
                              }}
                            />
                            <div className="w-full h-24 bg-gray-200 rounded-lg border flex items-center justify-center text-gray-500 text-sm" style={{display: 'none'}}>
                              Resim Yüklenemedi
                            </div>
                            <button
                              type="button"
                              onClick={() => removeImage(index)}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                            >
                              ×
                            </button>
                            {image.file ? (
                              <div className="absolute bottom-0 left-0 right-0 bg-green-500 text-white text-xs p-1 text-center">
                                Yeni
                              </div>
                            ) : (
                              <div className="absolute bottom-0 left-0 right-0 bg-blue-500 text-white text-xs p-1 text-center">
                                Mevcut
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    SKU *
                  </label>
                  <input
                    type="text"
                    name="sku"
                    value={formData.sku}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fiyat (₺) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Orijinal Fiyat (₺)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="original_price"
                    value={formData.original_price}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Stok *
                  </label>
                  <input
                    type="number"
                    name="stock"
                    value={formData.stock}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Açıklama *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div className="flex items-center space-x-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                  Aktif
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_featured"
                    checked={formData.is_featured}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                  Öne Çıkan
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="shipping_free"
                    checked={formData.shipping_free}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                  Ücretsiz Kargo
                </label>
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingProduct(null);
                    resetForm();
                  }}
                  className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  İptal
                </button>
                <LoadingButton
                  type="submit"
                  loading={loading}
                  className="px-6 py-2"
                >
                  {editingProduct ? 'Güncelle' : 'Ekle'}
                </LoadingButton>
              </div>
            </form>
          </div>
        )}

        {/* Arama ve Filtreler */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder="Ürün ara... (isim, marka, SKU)"
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div className="flex gap-2">
              <select
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="created_at">Eklenme Tarihi</option>
                <option value="name">İsim</option>
                <option value="price">Fiyat</option>
                <option value="stock">Stok</option>
              </select>
              
              <button
                onClick={() => {
                  setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
                  setCurrentPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {sortOrder === 'ASC' ? '↑' : '↓'}
              </button>
            </div>
          </div>
          
          {searchTerm && (
            <div className="mt-4 text-sm text-gray-600">
              "{searchTerm}" için {totalProducts} sonuç bulundu
            </div>
          )}
        </div>

        {/* Ürün Listesi */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Ürünler ({totalProducts})
            </h3>
          </div>
          
          {products.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">
              Henüz ürün eklenmemiş.
            </div>
          ) : (
            <div className="p-4">
              <div className="overflow-x-auto shadow-sm border border-gray-200 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200 table-fixed">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">
                      Ürün
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">
                      Açıklama
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                      Kategori
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/12">
                      Fiyat
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/12">
                      Stok
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/12">
                      Durum
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/12">
                      İşlemler
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {products.length > 0 ? products.map((product) => (
                    <tr key={product.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            {product.images && product.images.length > 0 ? (
                              <img
                                className="h-10 w-10 rounded-full object-cover"
                                src={getImageUrl(product.images[0].url)}
                                alt={product.name}
                                crossOrigin="anonymous"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center" style={{display: product.images && product.images.length > 0 ? 'none' : 'flex'}}>
                              <span className="text-gray-400 text-xs">Resim</span>
                            </div>
                          </div>
                          <div className="ml-4 min-w-0 flex-1">
                            <div 
                              className="text-sm font-medium text-gray-900 truncate cursor-help" 
                              title={product.name}
                            >
                              {product.name.length > 50 ? `${product.name.substring(0, 50)}...` : product.name}
                            </div>
                            <div className="text-sm text-gray-500 truncate">
                              {product.brand}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 max-w-xs">
                        <div 
                          className="whitespace-pre-line line-clamp-2 cursor-help" 
                          title={product.description}
                        >
                          {product.description.length > 100 ? `${product.description.substring(0, 100)}...` : product.description}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {product.categories ? (
                          <div className="flex flex-wrap gap-1">
                            {product.categories.map((category, index) => (
                              <span key={index} className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                                {category}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-500">Kategori yok</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        ₺{product.price.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {product.stock}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          product.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {product.is_active ? 'Aktif' : 'Pasif'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-3">
                          <button
                            onClick={() => handleEdit(product)}
                            className="text-indigo-600 hover:text-indigo-900 p-1 rounded hover:bg-indigo-50 transition-colors"
                            title="Düzenle"
                          >
                            <Edit className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(product.id)}
                            className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 transition-colors"
                            title="Sil"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                        Ürün bulunamadı
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Sayfa {currentPage} / {totalPages} (Toplam {totalProducts} ürün)
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      
                      {/* Sayfa numaraları */}
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`px-3 py-2 border rounded-lg ${
                              currentPage === pageNum
                                ? 'bg-blue-500 text-white border-blue-500'
                                : 'border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminProducts;

